import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  CheckCircle,
  XCircle,
  BookOpen,
  TrendingUp,
  AlertTriangle,
  Loader,
  ArrowRight,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { getPendingTest, clearPendingTest, PendingTestData } from "../../utils/pendingTestStorage";

const SkillsTestResults: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);

  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState<any>(null);
  const [mistakesData, setMistakesData] = useState<any>(null);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [generatingCourse, setGeneratingCourse] = useState(false);
  const [courseGenerationStatus, setCourseGenerationStatus] = useState<string>("");
  const [profileData, setProfileData] = useState<any>(null);
  const [courseGenerationAttempted, setCourseGenerationAttempted] = useState(false);
  const [pendingTest, setPendingTest] = useState<PendingTestData | null>(null);

  // Check for pending test on mount
  useEffect(() => {
    // Get pending test from navigation state or localStorage
    const pendingFromState = location.state?.pendingTest;
    if (pendingFromState) {
      setPendingTest(pendingFromState);
    } else {
      const pendingFromStorage = getPendingTest();
      setPendingTest(pendingFromStorage);
    }
  }, [location.state]);

  // Handle continue to profile for failed tests
  const handleContinueToProfile = () => {
    toast.error("Skills not verified. Please verify your skills before applying for jobs.");
    navigate("/job-seeker/profile");
  };

  // Handle proceeding to pending HR test
  const handleProceedToHRTest = () => {
    if (pendingTest) {
      clearPendingTest();
      navigate(`/job-seeker/take-test/${pendingTest.testId}`);
    }
  };

  useEffect(() => {
    const fetchResults = async () => {
      if (!testId || !user) return;

      try {

        // Check if test data was passed via navigation state (INSTANT - no Firestore read needed!)
        if (location.state?.testData) {
          const testData = location.state.testData;
          setTestResult(testData);
          setProfileData(location.state.profileData);
          setLoading(false);

          // Check if user made mistakes and generate course (only once!)
          if (testData.correctAnswers < testData.totalQuestions && !courseGenerationAttempted) {
            setCourseGenerationAttempted(true);
            generateCourse(testData);
          }
          return;
        }

        // Fallback: Fetch from Firestore (slower, for page refreshes)
        const testDoc = await getDoc(
          doc(db, "skillsValidationResults", testId)
        );

        if (!testDoc.exists()) {
          throw new Error("Test result not found");
        }

        const testData = { id: testDoc.id, ...testDoc.data() };

        setTestResult(testData);

        // Get profile data for course generation
        const profileDoc = await getDoc(doc(db, "userProfiles", user.id));
        setProfileData(profileDoc.data());

        setLoading(false);

        // Check if user made mistakes (not perfect score)
        if (testData.correctAnswers < testData.totalQuestions) {
          // Check if course already exists
          const mistakesDoc = await getDoc(doc(db, "testMistakes", testId));

          if (mistakesDoc.exists()) {
            // Course generation already completed
            setMistakesData(mistakesDoc.data());

            // Find the courseId by querying personalizedCourses collection
            const coursesQuery = query(
              collection(db, "personalizedCourses"),
              where("testResultId", "==", testId),
              limit(1)
            );
            const coursesSnapshot = await getDocs(coursesQuery);
            if (!coursesSnapshot.empty) {
              setCourseId(coursesSnapshot.docs[0].id);
            }
          } else {
            // Generate course in background
            generateCourse(testData);
          }
        }
      } catch (error) {
        toast.error("Failed to load test results");
        navigate("/job-seeker");
      }
    };

    fetchResults();
  }, [testId, user, db, navigate]);

  const generateCourse = async (testData: any) => {
    setGeneratingCourse(true);
    setCourseGenerationStatus("Analyzing your mistakes...");

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const idToken = await currentUser.getIdToken();


      // Use profileData from state if available, otherwise fetch from Firestore
      let userProfileData = profileData;
      if (!userProfileData) {
        const profileDoc = await getDoc(doc(db, "userProfiles", user!.id));
        userProfileData = profileDoc.data();
      }

      // Call analyzeTestMistakes function
      const mistakesResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/analyzeTestMistakes",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            testQuestions: testData.questions,
            userAnswers: testData.userAnswerIndices,
            correctAnswers: testData.correctAnswerIndices,
            resumeData: userProfileData?.resumeData,
          }),
        }
      );

      if (!mistakesResponse.ok) {
        const errorText = await mistakesResponse.text();
        throw new Error(`Failed to analyze mistakes: ${errorText}`);
      }

      const mistakesAnalysis = await mistakesResponse.json();

      // Store mistakes analysis
      await setDoc(doc(db, "testMistakes", testId!), {
        userId: user!.id,
        testResultId: testId,
        mistakes: mistakesAnalysis.mistakes,
        weakAreas: mistakesAnalysis.weakAreas,
        mistakePatterns: mistakesAnalysis.mistakePatterns,
        recommendedFocus: mistakesAnalysis.recommendedFocus,
        detailedAnalysis: mistakesAnalysis.detailedAnalysis,
        createdAt: Timestamp.now(),
      });
      setMistakesData(mistakesAnalysis);

      // Generate personalized course
      setCourseGenerationStatus("Creating your personalized learning course...");

      const courseResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generatePersonalizedCourse",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            mistakes: mistakesAnalysis.mistakes,
            weakAreas: mistakesAnalysis.weakAreas,
            recommendedFocus: mistakesAnalysis.recommendedFocus,
            resumeData: userProfileData?.resumeData,
          }),
        }
      );

      if (!courseResponse.ok) {
        const errorText = await courseResponse.text();
        throw new Error(`Failed to generate course: ${errorText}`);
      }

      const courseData = await courseResponse.json();

      // Store personalized course
      const courseRef = await addDoc(collection(db, "personalizedCourses"), {
        userId: user!.id,
        testResultId: testId,
        courseData: courseData.course,
        basedOnMistakes: mistakesAnalysis.weakAreas,
        completed: false,
        courseTestPassed: false,
        createdAt: Timestamp.now(),
      });

      setCourseId(courseRef.id);
      setGeneratingCourse(false);
      toast.success("🎉 Your personalized course is ready! Scroll down to start learning.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Course generation failed: ${errorMessage}`);
      setGeneratingCourse(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!testResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Results Not Found
          </h2>
          <button
            onClick={() => navigate("/job-seeker")}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const passed = testResult.score >= 60;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Score Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center">
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
                passed ? "bg-green-100" : "bg-red-100"
              }`}
            >
              {passed ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {passed ? "Congratulations!" : "Test Completed"}
            </h1>
            <p className="text-gray-600 mb-6">
              {passed
                ? "You passed the skills validation test!"
                : "You didn't pass this time, but don't worry!"}
            </p>

            <div className="inline-block bg-gray-100 rounded-lg px-8 py-4 mb-6">
              <p className="text-gray-600 text-sm mb-1">Your Score</p>
              <p className="text-5xl font-bold text-gray-900">
                {testResult.score}%
              </p>
              <p className="text-gray-600 text-sm mt-2">
                {testResult.correctAnswers} / {testResult.totalQuestions}{" "}
                correct
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 text-left">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p
                  className={`text-lg font-semibold ${
                    passed ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {passed ? "✓ Passed" : "✗ Not Passed"}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Passing Score</p>
                <p className="text-lg font-semibold text-gray-900">60%</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Time Spent</p>
                <p className="text-lg font-semibold text-gray-900">
                  {Math.floor(testResult.timeSpent / 60)}m{" "}
                  {testResult.timeSpent % 60}s
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Generation Status or Mistakes Analysis */}
        {(generatingCourse || mistakesData) && testResult.correctAnswers < testResult.totalQuestions && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {generatingCourse ? (
              // Show loading state while generating course
              <div className="text-center py-8">
                <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {courseGenerationStatus}
                </h2>
                <p className="text-gray-600">
                  Please wait while we create your personalized learning course...
                </p>
              </div>
            ) : mistakesData ? (
              // Show mistakes analysis and course CTA
              <>
                <div className="flex items-center space-x-3 mb-6">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Areas for Improvement
                  </h2>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      You made {mistakesData.mistakes.length} mistake(s)
                    </h3>
                    <p className="text-sm text-gray-700">
                      {mistakesData.detailedAnalysis}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Weak Areas Identified:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {mistakesData.weakAreas.map((area: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Recommended Focus:
                    </h3>
                    <ul className="space-y-2">
                      {mistakesData.recommendedFocus.map(
                        (topic: string, index: number) => (
                          <li key={index} className="flex items-start space-x-2">
                            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>

                {/* Personalized Course CTA or Retry Button */}
                {courseId ? (
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <BookOpen className="w-6 h-6" />
                          <h3 className="text-xl font-bold">
                            Your Personalized Learning Course is Ready!
                          </h3>
                        </div>
                        <p className="text-blue-100 mb-4">
                          We've created a custom course just for you based on your
                          mistakes. Learn the concepts you struggled with and
                          improve your skills before taking HR tests!
                        </p>
                        <button
                          onClick={() =>
                            navigate(`/job-seeker/personalized-course/${courseId}`)
                          }
                          className="flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
                        >
                          <BookOpen className="w-5 h-5" />
                          <span>Start Learning Now</span>
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : !generatingCourse && mistakesData ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                      Course Generation Issue
                    </h3>
                    <p className="text-yellow-800 mb-4">
                      Your personalized course hasn't been generated yet. Click below to generate it now.
                    </p>
                    <button
                      onClick={() => generateCourse(testResult)}
                      className="flex items-center space-x-2 bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors font-semibold"
                    >
                      <BookOpen className="w-5 h-5" />
                      <span>Generate My Course Now</span>
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        )}

        {/* Perfect Score Message */}
        {testResult.score === 100 && testResult.correctAnswers === testResult.totalQuestions && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Perfect Score!
              </h2>
              <p className="text-gray-600">
                You answered all questions correctly. Great job! You're ready to
                take HR tests.
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col items-center space-y-4">
          {/* Primary action based on pass/fail and pending test */}
          {passed ? (
            // User passed the test
            pendingTest ? (
              // There's a pending HR test - show button to take it
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 w-full max-w-md text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  You're Verified!
                </h3>
                <p className="text-green-800 text-sm mb-4">
                  You can now take the {pendingTest.testTitle || "HR"} test that was shared with you.
                </p>
                <button
                  onClick={handleProceedToHRTest}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  <span>Take HR Test Now</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            ) : (
              // No pending test - show normal options
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate("/job-seeker")}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => navigate("/job-seeker/tests")}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  Browse HR Tests
                </button>
              </div>
            )
          ) : (
            // User failed the test
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 w-full max-w-md text-center">
              <XCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                Skills Not Verified
              </h3>
              <p className="text-red-800 text-sm mb-4">
                You need a score of 60% or higher to get verified. You can retake the test from your profile.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleContinueToProfile}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  <User className="w-5 h-5" />
                  <span>Continue to Profile</span>
                </button>
                <button
                  onClick={() => navigate("/job-seeker/skills-validation")}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Retake Test Now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillsTestResults;
